import React, { useEffect, useMemo, useState } from "react";
import { useMarket } from "../../context/MarketContext";
import { meta, fmtPrice } from "../../lib/symbols";
import { SymbolCatalog } from "../../engine";
import { Star, ChevronDown, ChevronRight, Search } from "lucide-react";

// Watchlist driven by Deriv's live active_symbols catalogue: "Favourites" + one tab per market, grouped by submarket.
export default function Watchlist({ favorites, onToggleFav, onSelect, current }) {
  const { quotes, subscribeExtra } = useMarket();
  const [tab, setTab] = useState("fav");
  const [q, setQ] = useState("");
  const [catalog, setCatalog] = useState({ symbols: SymbolCatalog.all(), markets: SymbolCatalog.markets() });
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => SymbolCatalog.onLoad(() => setCatalog({ symbols: SymbolCatalog.all(), markets: SymbolCatalog.markets() })), []);

  const tabs = useMemo(() => [{ id: "fav", label: "Favourites" }, ...catalog.markets.map((m) => ({ id: m.market, label: m.display }))], [catalog.markets]);
  const query = q.trim().toLowerCase();

  const groups = useMemo(() => {
    if (query) {
      const hits = catalog.symbols.filter((s) => s.symbol.toLowerCase().includes(query) || s.name.toLowerCase().includes(query) || s.display.toLowerCase().includes(query)).slice(0, 40);
      return [{ id: "search", label: `Search · ${hits.length}`, symbols: hits.map((s) => s.symbol) }];
    }
    if (tab === "fav") return [{ id: "fav", label: "Favourites", symbols: favorites }];
    const m = catalog.markets.find((x) => x.market === tab);
    return (m?.submarkets || []).map((sm) => ({ id: sm.submarket, label: sm.display, symbols: sm.symbols }));
  }, [tab, query, favorites, catalog]);

  // quotes for whatever is visible (shared tick bus; released when the tab changes)
  useEffect(() => { subscribeExtra(groups.flatMap((g) => (collapsed[g.id] ? [] : g.symbols)).slice(0, 24)); return () => subscribeExtra([]); }, [groups, collapsed, subscribeExtra]);

  return (
    <div className="flex flex-col bg-panel border border-border rounded-xl overflow-hidden" data-testid="watchlist">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <h3 className="font-heading font-semibold text-base">Markets</h3>
        <span className="text-[10px] text-muted-foreground" data-testid="watchlist-count">{catalog.symbols.length ? `${catalog.symbols.length} Deriv instruments` : "loading catalogue…"}</span>
      </div>
      <div className="px-2 py-1.5 border-b border-border relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input data-testid="watchlist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search instruments…" className="w-full h-8 pl-8 pr-2 rounded-md bg-background border border-border text-xs outline-none focus:border-primary/60" />
      </div>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border overflow-x-auto scrollbar-thin">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`wl-tab-${t.id}`} onClick={() => { setTab(t.id); setQ(""); }}
            className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${tab === t.id && !query ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
        <span>Symbol</span><span className="text-right pr-4">Last</span><span className="text-right">Chg %</span>
      </div>
      <div className="overflow-y-auto scrollbar-thin max-h-[320px]" data-testid="watchlist-rows">
        {groups.map((g) => (
          <div key={g.id}>
            {groups.length > 1 && (
              <button data-testid={`wl-group-${g.id}`} onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))} className="w-full flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wide text-primary/90 bg-secondary/40">
                {collapsed[g.id] ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}{g.label} <span className="text-muted-foreground ml-auto">{g.symbols.length}</span>
              </button>
            )}
            {!collapsed[g.id] && g.symbols.map((code) => <Row key={code} code={code} q={quotes[code]} fav={favorites.includes(code)} current={current === code} onSelect={onSelect} onToggleFav={onToggleFav} />)}
            {g.symbols.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground text-center" data-testid="wl-empty">Star instruments from any market tab to build your watchlist.</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ code, q, fav, current, onSelect, onToggleFav }) {
  const m = meta(code);
  const up = (q?.changePct ?? 0) >= 0;
  return (
    <div data-testid={`wl-row-${code}`} onClick={() => onSelect(code)}
      className={`grid grid-cols-[1fr_auto_auto] items-center px-3 py-2 cursor-pointer border-b border-border/50 hover:bg-secondary/60 ${current ? "bg-primary/10" : ""} ${m.open === false ? "opacity-60" : ""}`}>
      <span className="flex items-center gap-2 min-w-0">
        <button data-testid={`wl-fav-${code}`} onClick={(e) => { e.stopPropagation(); onToggleFav(code); }} className={fav ? "text-primary" : "text-muted-foreground hover:text-primary"}>
          <Star className="h-3.5 w-3.5" fill={fav ? "currentColor" : "none"} />
        </button>
        <span className="min-w-0"><span className="text-xs font-semibold truncate block">{m.display}</span><span className="text-[10px] text-muted-foreground truncate block">{m.name}{m.open === false ? " · closed" : ""}</span></span>
      </span>
      <span className="text-right pr-4 text-xs font-mono-nums">{q ? fmtPrice(q.price, m.decimals) : "—"}</span>
      <span className={`text-right text-xs font-mono-nums ${up ? "text-buy" : "text-sell"}`}>{up ? "+" : ""}{(q?.changePct ?? 0).toFixed(2)}%</span>
    </div>
  );
}
